import * as React from 'react';
import FieldsInterface from '@app/Interface/FieldsInterface';
import UserAuthentication from '@app/Security/UserAuthentication';
import { ActionGroup, Alert, Button, Form, FormAlert, FormGroup, TextInput, Title, Switch, ToggleGroup, ToggleGroupItem } from '@patternfly/react-core';
import { ExclamationCircleIcon } from '@patternfly/react-icons';
import { Link } from 'react-router-dom';
import ItemLoader from '../ItemLoader';
import RegistrationFormFunctions from './RegistrationForm/RegistrationFormFunctions';
import RegistrationHelper from '@app/Service/RegistrationHelper';
import UserRegistrationDataModel from '@app/Model/UserRegistrationDataModel';
import PhoneInput from "react-phone-number-input/input";
import { E164Number } from 'libphonenumber-js/min';
import UserVerifyAuthFactorModal from '@app/Components/User/UserVerifyAuthFactorModal';

interface RegistrationFormProps {
    userId: number;
    jwt: string;
    handleReturnToLogin: () => void;
}

const RegistrationForm: React.FC<RegistrationFormProps> = ({ userId, jwt, handleReturnToLogin }) => {

    const [validJwt, setValidJwt] = React.useState(false);

    const [isLoading, setIsLoading] = React.useState(true);

    const [newAuthFactorVerified, setNewAuthFactorVerified] = React.useState(false);

    const [isVerifyModalOpen, setIsVerifyModalOpen] = React.useState(false);

    const initialFields = RegistrationFormFunctions.getRegistrationFields();

    const [formFields, setFormFields] = React.useState<FieldsInterface>(initialFields);

    const defaultFormValidated = "default";

    // for values: see https://www.patternfly.org/v4/components/form#formgroup
    const [formValidated, setFormValidated] = React.useState(defaultFormValidated);

    const [formValidationTitle, setFormValidationTitle] = React.useState("Required Fields");

    const [formValidationMessage, setFormValidationMessage] = React.useState("Fill out all required fields before continuing.");

    const [isFormProcessing, setIsFormProcessing] = React.useState(false);

    const handleChange = (newValue: string | boolean, e: React.FormEvent<HTMLInputElement>) => {

        const fieldId = e.currentTarget.id;

        const updatedFields = { ...formFields };

        updatedFields[fieldId].value = newValue;

        setFormFields(updatedFields);

        setNewAuthFactorVerified(false);

    }

    const handlePhoneChange = (newValue: E164Number, fieldId: string) => {

        const updatedFields = { ...formFields };

        updatedFields[fieldId].value = String(newValue);

        setFormFields(updatedFields);

        setNewAuthFactorVerified(false);

    }

    // correct event typing is `MouseEvent | React.MouseEvent<Element, MouseEvent> | React.KeyboardEvent<Element>`
    // TODO: see if there's a way to replace `any` with typing above
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const handleNoticeTypeClick = (isSelected: boolean, event: any) => {

        const fieldId = event.currentTarget.id;

        const updatedFields = { ...formFields };

        if (isSelected && fieldId === formFields["twoFactorType"].fieldId + "-email") {

            formFields["twoFactorType"].value = "email";

            setFormFields(updatedFields);


        } else if (isSelected && fieldId === formFields["twoFactorType"].fieldId + "-sms") {

            formFields["twoFactorType"].value = "sms";

            setFormFields(updatedFields);

        }

        setNewAuthFactorVerified(false);

    }

    const registerUser = async (updatedFields: FieldsInterface) => {
        const registrationData = new UserRegistrationDataModel();

        registrationData.jwt = jwt;
        registrationData.password = String(updatedFields["password"].value);
        registrationData.enableTwoFactor = updatedFields["enableTwoFactor"].value === true ?? false;
        registrationData.twoFactorType = String(updatedFields["twoFactorType"].value);
        registrationData.twoFactorEmailAddress = String(updatedFields["twoFactorEmailAddress"].value);
        registrationData.twoFactorPhoneNumber = String(updatedFields["twoFactorPhoneNumber"].value);
        registrationData.firstName = String(updatedFields["firstName"].value);
        registrationData.lastName = String(updatedFields["lastName"].value);
        registrationData.address = String(updatedFields["address1"].value);
        registrationData.address2 = String(updatedFields["address2"].value);
        registrationData.city = String(updatedFields["city"].value);
        registrationData.state = String(updatedFields["state"].value);
        registrationData.zip = String(updatedFields["zip"].value);
        
        const responseCode = await RegistrationHelper.submitUserRegistration(userId, registrationData);

        switch (responseCode) {

            case 201:
                
                // clear form values
                updatedFields["password"].value = "";
                updatedFields["confirmPassword"].value = "";

                setFormFields(updatedFields);

                setFormValidated("success");

                break;

            case 400:
                setFormValidated("error");
                setFormValidationTitle("Invalid Input");
                setFormValidationMessage("Please try again or contact support if the issue persists.");
                break;

            case 404:
                setFormValidated("error");
                setFormValidationTitle("Resource not found");
                setFormValidationMessage("Endpoint to register account is invalid, try again or contact support.");
                break;

            case 422:
                setFormValidated("error");
                setFormValidationTitle("Bad user data");
                setFormValidationMessage("Unknown data error, try again or contact support.");
                break;

            default:
                setFormValidated("error");
                setFormValidationTitle("Unknown error");
                setFormValidationMessage("Unable to register account, try again or contact support.");
                break;

        }
    };

    const handleSubmit = async (event: React.SyntheticEvent) => {

        event.preventDefault();

        setIsFormProcessing(true);

        setFormValidated("default");

        const [validForm, updatedFields] = RegistrationFormFunctions.formValidation(formFields);

        if (validForm && newAuthFactorVerified) {
            registerUser(updatedFields);
        } else if (validForm) {
            setIsVerifyModalOpen(true);
        } else {

            setFormFields(updatedFields);

            setFormValidated("error");

            setFormValidationTitle("Required Fields");
                    
            setFormValidationMessage("Fill out all required fields before continuing.");
                    
        }

        setIsFormProcessing(false);

        window.scrollTo(0, 0);

    }

    React.useEffect(() => {

        const controller = new AbortController();

        let mounted = true;

        async function validateUserJwt() {

            const valid = await UserAuthentication.validateJwt(controller, userId, jwt);

            if (mounted) {
                setValidJwt(valid);

                setIsLoading(false);    
            }
            
        }

        setIsLoading(true);

        validateUserJwt();

        return () => {
            mounted = false;
            controller?.abort();
        };

    }, [userId, jwt]);

    React.useEffect(() => {
        const controller = new AbortController();

        let mounted = true;

        const maybeSubmitForm = async () => {
            if (mounted && newAuthFactorVerified) {
                const [validForm, updatedFields] = RegistrationFormFunctions.formValidation(formFields);
                if (validForm) {
                    registerUser(updatedFields);
                } else {
                    setFormFields(updatedFields);
                    setFormValidated("error");
                    setFormValidationTitle("Required Fields");
                    setFormValidationMessage("Fill out all required fields before continuing.");
                }
            }
        }

        maybeSubmitForm();

        return () => {
            mounted = false;
            controller?.abort();
        };
    }, [newAuthFactorVerified]);

    if (isLoading) {

        return (

            <Form>
                <ItemLoader />
            </Form>

        );

    }

    if (!validJwt) {

        return (
            <Form>
                <FormAlert>
                    <Alert
                        variant="danger"
                        title="Invalid Token"
                        role="alert"
                    >
                        <p>The token was invalid. Please try clicking the link in your email again or use the forgot password link below.</p>
                    </Alert>
                </FormAlert>
            </Form>
        );
    }

    if (validJwt && formValidated === "success") {

        return (
            <Form>
                <FormAlert>
                    <Alert
                        variant="success"
                        title="Your account was successfully registered. You may try to login now."
                        aria-live="polite"
                        isInline={true}
                        role="alert"
                    >
                        <Link to="/" onClick={handleReturnToLogin}>Login</Link>
                    </Alert>
                </FormAlert>
            </Form>
        );

    }

    return (
        <>
            <UserVerifyAuthFactorModal 
                isModalOpen={isVerifyModalOpen}
                typeToVerify={`${formFields["twoFactorType"].value}`}
                deliveryMethodToVerify={formFields["twoFactorType"].value === 'email'
                    ? `${formFields["twoFactorEmailAddress"].value}`
                    : `${formFields["twoFactorPhoneNumber"].value}`
                }
                registeringUserId={userId}
                registeringUserJWT={jwt}
                handleFactorValidated={() => {
                    setIsVerifyModalOpen(false);
                    setNewAuthFactorVerified(true);
                }}
                handleCloseClicked={() => {
                    setIsVerifyModalOpen(false);
                }}
            />

            <Form autoComplete="false" isWidthLimited onSubmit={handleSubmit}>

                <Title headingLevel="h2" className="pf-u-mb-0">New Account Registration</Title>

                {formValidated === "error" && (
                    <FormAlert>
                        <Alert
                            variant="danger"
                            title={formValidationTitle}
                            aria-live="polite"
                            role="alert"
                            isInline
                        >{formValidationMessage}</Alert>
                    </FormAlert>
                )}

                <Title headingLevel="h3" className="pf-u-mb-0">Credentials</Title>

                <FormGroup
                    label={formFields["password"].label}
                    isRequired={formFields["password"].required}
                    fieldId={formFields["password"].formFieldId}
                    helperTextInvalid={formFields["password"].helperTextInvalid}
                    helperTextInvalidIcon={<ExclamationCircleIcon />}
                    validated={formFields["password"].validated}
                >
                    <TextInput
                        isRequired={formFields["password"].required}
                        type="password"
                        id={formFields["password"].fieldId}
                        name={formFields["password"].fieldId}
                        value={typeof formFields["password"].value !== "boolean" ? formFields["password"].value : ""}
                        onChange={handleChange}
                    />
                </FormGroup>

                <FormGroup
                    label={formFields["confirmPassword"].label}
                    isRequired={formFields["confirmPassword"].required}
                    fieldId={formFields["confirmPassword"].formFieldId}
                    helperTextInvalid={formFields["confirmPassword"].helperTextInvalid}
                    helperTextInvalidIcon={<ExclamationCircleIcon />}
                    validated={formFields["confirmPassword"].validated}
                >
                    <TextInput
                        isRequired={formFields["confirmPassword"].required}
                        type="password"
                        id={formFields["confirmPassword"].fieldId}
                        name={formFields["confirmPassword"].fieldId}
                        value={typeof formFields["confirmPassword"].value === "string" ? formFields["confirmPassword"].value : ""}
                        onChange={handleChange}
                    />
                </FormGroup>

                <Title headingLevel="h3" className="pf-u-mb-0">Two Factor Authentication Settings</Title>

                {typeof formFields["enableTwoFactor"].value === "boolean" && formFields["enableTwoFactor"].value === true &&
                    <FormGroup
                        label={formFields["twoFactorType"].label}
                        isRequired={formFields["twoFactorType"].required}
                        fieldId={formFields["twoFactorType"].formFieldId}
                        helperTextInvalid={formFields["twoFactorType"].helperTextInvalid}
                        helperTextInvalidIcon={<ExclamationCircleIcon />}
                        validated={formFields["twoFactorType"].validated}
                    >
                        <ToggleGroup aria-label="Verification Type">
                            <ToggleGroupItem
                                text="Email"
                                buttonId={formFields["twoFactorType"].fieldId + "-email"}
                                isSelected={formFields["twoFactorType"].value === "email"}
                                onChange={handleNoticeTypeClick}
                            />
                            <ToggleGroupItem
                                text="SMS"
                                buttonId={formFields["twoFactorType"].fieldId + "-sms"}
                                isSelected={formFields["twoFactorType"].value === "sms"}
                                onChange={handleNoticeTypeClick}
                            />
                        </ToggleGroup>
                    </FormGroup>
                }

                {typeof formFields["enableTwoFactor"].value === "boolean" && formFields["enableTwoFactor"].value === true && formFields["twoFactorType"].value === "email" &&
                    <FormGroup
                        label={formFields["twoFactorEmailAddress"].label}
                        isRequired={formFields["twoFactorEmailAddress"].required}
                        fieldId={formFields["twoFactorEmailAddress"].formFieldId}
                        helperTextInvalid={formFields["twoFactorEmailAddress"].helperTextInvalid}
                        helperTextInvalidIcon={<ExclamationCircleIcon />}
                        validated={formFields["twoFactorEmailAddress"].validated}
                    >
                        <TextInput
                            isRequired={formFields["twoFactorEmailAddress"].required}
                            type="email"
                            id={formFields["twoFactorEmailAddress"].fieldId}
                            name={formFields["twoFactorEmailAddress"].fieldId}
                            value={typeof formFields["twoFactorEmailAddress"].value === "string" ? formFields["twoFactorEmailAddress"].value : ""}
                            onChange={handleChange}
                        />
                    </FormGroup>
                }
                {typeof formFields["enableTwoFactor"].value === "boolean" && formFields["enableTwoFactor"].value === true && formFields["twoFactorType"].value === "sms" &&
                    <FormGroup
                        label={formFields["twoFactorPhoneNumber"].label}
                        isRequired={formFields["twoFactorPhoneNumber"].required}
                        fieldId={formFields["twoFactorPhoneNumber"].formFieldId}
                        helperTextInvalid={formFields["twoFactorPhoneNumber"].helperTextInvalid}
                        helperTextInvalidIcon={<ExclamationCircleIcon />}
                        validated={formFields["twoFactorPhoneNumber"].validated}
                    >
                        <PhoneInput
                                country='US'
                                id={formFields["twoFactorPhoneNumber"].fieldId}
                                name={formFields["twoFactorPhoneNumber"].fieldId}
                                value={typeof formFields["twoFactorPhoneNumber"].value === "string" ? formFields["twoFactorPhoneNumber"].value : ""}
                                onChange={(value: E164Number) => handlePhoneChange(value, formFields["twoFactorPhoneNumber"].fieldId)}
                            />
                    </FormGroup>
                }

                <Title headingLevel="h3" className="pf-u-mb-0">Contact Information</Title>

                <FormGroup
                    label={formFields["firstName"].label}
                    isRequired={formFields["firstName"].required}
                    fieldId={formFields["firstName"].formFieldId}
                    helperTextInvalid={formFields["firstName"].helperTextInvalid}
                    helperTextInvalidIcon={<ExclamationCircleIcon />}
                    validated={formFields["firstName"].validated}
                >
                    <TextInput
                        isRequired={formFields["firstName"].required}
                        type="text"
                        id={formFields["firstName"].fieldId}
                        name={formFields["firstName"].fieldId}
                        value={typeof formFields["firstName"].value !== "boolean" ? formFields["firstName"].value : ""}
                        onChange={handleChange}
                    />
                </FormGroup>

                <FormGroup
                    label={formFields["lastName"].label}
                    isRequired={formFields["lastName"].required}
                    fieldId={formFields["lastName"].formFieldId}
                    helperTextInvalid={formFields["lastName"].helperTextInvalid}
                    helperTextInvalidIcon={<ExclamationCircleIcon />}
                    validated={formFields["lastName"].validated}
                >
                    <TextInput
                        isRequired={formFields["lastName"].required}
                        type="text"
                        id={formFields["lastName"].fieldId}
                        name={formFields["lastName"].fieldId}
                        value={typeof formFields["lastName"].value !== "boolean" ? formFields["lastName"].value : ""}
                        onChange={handleChange}
                    />
                </FormGroup>

                <FormGroup
                    label={formFields["address1"].label}
                    isRequired={formFields["address1"].required}
                    fieldId={formFields["address1"].formFieldId}
                    helperTextInvalid={formFields["address1"].helperTextInvalid}
                    helperTextInvalidIcon={<ExclamationCircleIcon />}
                    validated={formFields["address1"].validated}
                >
                    <TextInput
                        isRequired={formFields["address1"].required}
                        type="text"
                        id={formFields["address1"].fieldId}
                        name={formFields["address1"].fieldId}
                        value={typeof formFields["address1"].value !== "boolean" ? formFields["address1"].value : ""}
                        onChange={handleChange}
                    />
                </FormGroup>

                <FormGroup
                    label={formFields["address2"].label}
                    isRequired={formFields["address2"].required}
                    fieldId={formFields["address2"].formFieldId}
                    helperTextInvalid={formFields["address2"].helperTextInvalid}
                    helperTextInvalidIcon={<ExclamationCircleIcon />}
                    validated={formFields["address2"].validated}
                >
                    <TextInput
                        isRequired={formFields["address2"].required}
                        type="text"
                        id={formFields["address2"].fieldId}
                        name={formFields["address2"].fieldId}
                        value={typeof formFields["address2"].value !== "boolean" ? formFields["address2"].value : ""}
                        onChange={handleChange}
                    />
                </FormGroup>

                <FormGroup
                    label={formFields["city"].label}
                    isRequired={formFields["city"].required}
                    fieldId={formFields["city"].formFieldId}
                    helperTextInvalid={formFields["city"].helperTextInvalid}
                    helperTextInvalidIcon={<ExclamationCircleIcon />}
                    validated={formFields["city"].validated}
                >
                    <TextInput
                        isRequired={formFields["city"].required}
                        type="text"
                        id={formFields["city"].fieldId}
                        name={formFields["city"].fieldId}
                        value={typeof formFields["city"].value !== "boolean" ? formFields["city"].value : ""}
                        onChange={handleChange}
                    />
                </FormGroup>

                <FormGroup
                    label={formFields["state"].label}
                    isRequired={formFields["state"].required}
                    fieldId={formFields["state"].formFieldId}
                    helperTextInvalid={formFields["state"].helperTextInvalid}
                    helperTextInvalidIcon={<ExclamationCircleIcon />}
                    validated={formFields["state"].validated}
                >
                    <TextInput
                        isRequired={formFields["state"].required}
                        type="text"
                        id={formFields["state"].fieldId}
                        name={formFields["state"].fieldId}
                        value={typeof formFields["state"].value !== "boolean" ? formFields["state"].value : ""}
                        onChange={handleChange}
                    />
                </FormGroup>

                <FormGroup
                    label={formFields["zip"].label}
                    isRequired={formFields["zip"].required}
                    fieldId={formFields["zip"].formFieldId}
                    helperTextInvalid={formFields["zip"].helperTextInvalid}
                    helperTextInvalidIcon={<ExclamationCircleIcon />}
                    validated={formFields["zip"].validated}
                >
                    <TextInput
                        isRequired={formFields["zip"].required}
                        type="text"
                        id={formFields["zip"].fieldId}
                        name={formFields["zip"].fieldId}
                        value={typeof formFields["zip"].value !== "boolean" ? formFields["zip"].value : ""}
                        onChange={handleChange}
                    />
                </FormGroup>

                <ActionGroup>
                    <Button
                        variant="primary"
                        onClick={handleSubmit}
                        spinnerAriaValueText={isFormProcessing ? "Registering" : undefined}
                        isLoading={isFormProcessing}
                    >{isFormProcessing ? "Registering" : "Register"}</Button>
                </ActionGroup>

            </Form>
        </>
    );

}

export default RegistrationForm;